/**
 * Targets panel — Phase 5 C1.
 *
 * Voyage-level (not per-day) success metrics. Reads/writes `voyage.targets[]`.
 * Each row: name + value + optional unit + remove. "+ Add target" lets the
 * user pick a library target (snapshots name + unit) or compose a custom one.
 * Persisted via `updateVoyage(id, { targets })` on Save (handled by parent).
 */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, X, Target } from "lucide-react";
import { cn } from "@/lib/utils";

export default function TargetsPanel({ value, onChange, targetsLib }) {
  const list = value || [];
  const [picker, setPicker] = useState(null); // null | "lib" | "custom"

  // Phase 7 — work out which library targets are NOT yet on this voyage so
  // the "Sync from library" button can pull in the missing ones in one go.
  // We match on `target_id` (lib id) first, falling back to name match for
  // legacy rows that were added before target_id was tracked.
  const onVoyageIds = new Set(list.map((t) => t.target_id).filter(Boolean));
  const onVoyageNames = new Set(
    list
      .filter((t) => !t.target_id)
      .map((t) => (t.name || "").trim().toLowerCase())
      .filter(Boolean),
  );
  const missingFromLib = (targetsLib || []).filter(
    (t) =>
      !onVoyageIds.has(t.id) &&
      !onVoyageNames.has((t.name || "").trim().toLowerCase()),
  );
  const allLibSynced =
    (targetsLib || []).length > 0 && missingFromLib.length === 0;

  const update = (idx, patch) => {
    onChange(list.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };
  const remove = (idx) => {
    onChange(list.filter((_, i) => i !== idx));
  };

  // Phase 8 code-quality — every target row gets a stable client-side
  // key on creation so React's reconciler doesn't shuffle inputs when
  // the user reorders/deletes. `_key` is stripped server-side by the
  // strict Pydantic models (extra="ignore") — purely a UI artefact.
  const _newKey = () => (
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `t_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  );

  const addFromLib = (libId) => {
    const t = (targetsLib || []).find((x) => x.id === libId);
    if (!t) return;
    onChange([
      ...list,
      { _key: _newKey(), target_id: t.id, name: t.name, value: "", unit: t.unit || "" },
    ]);
    setPicker(null);
  };

  const addCustom = () => {
    onChange([...list, { _key: _newKey(), target_id: null, name: "", value: "", unit: "" }]);
    setPicker(null);
  };

  const syncFromLib = () => {
    // Phase 7 — append every missing library target with an empty value
    // so the user only has to fill in the numbers.
    const additions = missingFromLib.map((t) => ({
      _key: _newKey(),
      target_id: t.id,
      name: t.name,
      value: "",
      unit: t.unit || "",
    }));
    onChange([...list, ...additions]);
  };

  return (
    <section
      className="rounded-lg border border-sky-200 bg-sky-50/40 p-3"
      data-testid="targets-panel"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Target className="h-3.5 w-3.5 text-sky-700" />
          <h3 className="text-xs font-bold uppercase tracking-widest text-sky-900">
            Voyage Targets
          </h3>
          <span className="text-[10px] text-muted-foreground">
            ({list.length})
          </span>
        </div>

        {/* Add picker */}
        {picker === null ? (
          <div className="flex items-center gap-1.5">
            {missingFromLib.length > 0 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={syncFromLib}
                className="h-8 border-emerald-300 bg-emerald-50 px-2 text-xs text-emerald-900 hover:bg-emerald-100"
                data-testid="sync-targets-from-lib-btn"
                title={`Pull ${missingFromLib.length} library target${missingFromLib.length === 1 ? "" : "s"} that aren't on this voyage yet.`}
              >
                <Plus className="h-3.5 w-3.5" />
                Sync from library ({missingFromLib.length})
              </Button>
            )}
            {allLibSynced ? (
              <span
                className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700"
                data-testid="targets-all-synced"
                title="Every library target is already on this voyage."
              >
                ✓ All library targets synced
              </span>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setPicker("lib")}
                data-testid="add-target-btn"
              >
                <Plus className="h-3.5 w-3.5" />
                Add target
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <Select
              value=""
              onValueChange={(v) => {
                if (v === "__custom__") return addCustom();
                addFromLib(v);
              }}
            >
              <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="add-target-select">
                <SelectValue placeholder="Pick from library…" />
              </SelectTrigger>
              <SelectContent>
                {(targetsLib || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.unit ? ` (${t.unit})` : ""}
                  </SelectItem>
                ))}
                <SelectItem value="__custom__">+ Custom…</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setPicker(null)}
              className="h-8 px-2 text-xs"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {list.length === 0 ? (
        <p className="text-xs italic text-muted-foreground">
          No targets yet — click "Add target" to define a voyage KPI.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {list.map((t, idx) => (
            <div
              // Stable key: prefer the client-stamped `_key` (set on
              // creation), then the lib `target_id`, and only fall back
              // to `idx` for legacy rows that don't have either (no
              // backend migration needed — those rows still render).
              key={t._key || t.target_id || `legacy-${idx}`}
              className={cn(
                "flex items-center gap-1.5 rounded-md border bg-white px-2 py-1 shadow-sm",
              )}
              data-testid={`target-row-${idx}`}
            >
              <Input
                value={t.name || ""}
                onChange={(e) => update(idx, { name: e.target.value })}
                placeholder="Name"
                className="h-7 w-[160px] text-xs"
                data-testid={`target-name-${idx}`}
              />
              <span className="text-xs text-muted-foreground">:</span>
              <Input
                value={t.value || ""}
                onChange={(e) => update(idx, { value: e.target.value })}
                placeholder="value"
                className="h-7 w-[80px] text-xs"
                data-testid={`target-value-${idx}`}
              />
              <Input
                value={t.unit || ""}
                onChange={(e) => update(idx, { unit: e.target.value })}
                placeholder="unit"
                className="h-7 w-[60px] text-xs"
                data-testid={`target-unit-${idx}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => remove(idx)}
                className="h-6 w-6 text-destructive hover:bg-destructive/10"
                data-testid={`target-remove-${idx}`}
                aria-label="Remove target"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <p className="mt-2 text-[10px] text-muted-foreground">
        Voyage-level metrics — applies to the whole voyage, not this single day.
        Saved with the voyage header (not the day's meal plan).
      </p>
    </section>
  );
}
